import React, { useState, useCallback, useRef } from 'react';
import proptypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { SwatchComponent } from './swatch-component';
import {
  getMouseEnterHandler, getMouseLeaveHandler,
  shouldShowTooltipCondition, shouldShowUnavailableTooltipCondition
} from '../../super-sku-util';

const {
  bool, func, oneOf,
  shape, string, number
} = proptypes;

const getTitle = (shouldShowTooltip, value) => (shouldShowTooltip ? '' : value);

const shouldShowImpressionData = (index, isImpressionsRequired,
  isHovered, isClickedMobile) => (index !== 0 && isImpressionsRequired && (isHovered || isClickedMobile));

const createImpressionData = (swatchItemId, componentImpressionData) => ({
  id: swatchItemId || '',
  name: componentImpressionData?.name || '',
  component: componentImpressionData?.component || '',
  position: componentImpressionData?.position || 1,
  type: componentImpressionData?.type || '',
  category: 'ssku'
});

export const Swatch = (props) => {
  const {
    channel,
    displayInToolTip,
    hoveredCombinationDetails,
    isAvailable,
    isSelected,
    isTooltipsEnabled,
    miniSwatch,
    onClick,
    onMouseEnter,
    onMouseLeave,
    swatchGuid,
    size,
    value,
    index,
    swatchItemId,
    componentImpressionData,
    isImpressionsRequired,
    unavailableTooltipMessage,
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [isClickedMobile, setIsClickedMobile] = useState(false);
  const anchorRef = useRef(null);

  const isMobile = channel === 'mobile';

  const shouldShowTooltip = shouldShowTooltipCondition(isHovered, isTooltipsEnabled,
    hoveredCombinationDetails, miniSwatch);

  const shouldShowUnavailableTooltip = shouldShowUnavailableTooltipCondition(unavailableTooltipMessage, isHovered,
    isAvailable, isTooltipsEnabled,
    hoveredCombinationDetails, miniSwatch);

  const title = getTitle(shouldShowTooltip, value);

  const mouseEnter = useCallback(
    getMouseEnterHandler(isMobile, setIsHovered, onMouseEnter, anchorRef),
    [isMobile, onMouseEnter]
  );

  const mouseLeave = useCallback(
    getMouseLeaveHandler(isMobile, setIsHovered, onMouseLeave, anchorRef),
    [isMobile, onMouseLeave]
  );

  const handleClick = useCallback((event) => {
    onClick(event);
    if (isMobile) setIsClickedMobile(true);
  }, [onClick, isMobile]);

  const { ref: impressionRef, clickID: impressionClickID } = useImpression({
    data: createImpressionData(swatchItemId, componentImpressionData)
  });

  const isShowImpressionData = shouldShowImpressionData(index, isImpressionsRequired, isHovered, isClickedMobile);

  const impressionProps = isShowImpressionData ? {
    ref: impressionRef,
    clickid: impressionClickID
  } : {};

  return (
    <SwatchComponent
      value={value}
      isSelected={isSelected}
      isAvailable={isAvailable}
      miniSwatch={miniSwatch}
      swatchGuid={swatchGuid}
      displayInToolTip={displayInToolTip}
      title={title}
      onSwatchClick={handleClick}
      onSwatchMouseEnter={mouseEnter}
      onSwatchMouseLeave={mouseLeave}
      impressionProps={impressionProps}
      showMainTooltip={shouldShowTooltip}
      showUnavailableTooltip={shouldShowUnavailableTooltip}
      anchorRef={anchorRef}
      hoveredCombinationDetails={hoveredCombinationDetails}
      onPopoverClose={mouseLeave}
      unavailableTooltipMessage={unavailableTooltipMessage}
    />
  );
};

Swatch.displayName = 'Swatch';

Swatch.propTypes = {
  channel: oneOf(['desktop', 'mobile']).isRequired,
  displayInToolTip: bool,
  hoveredCombinationDetails: shape({}),
  isAvailable: bool.isRequired,
  isSelected: bool.isRequired,
  isTooltipsEnabled: bool,
  miniSwatch: bool,
  onClick: func.isRequired,
  onMouseEnter: func.isRequired,
  onMouseLeave: func.isRequired,
  size: string,
  swatchGuid: string.isRequired,
  value: string.isRequired,
  swatchItemId: string,
  index: number,
  componentImpressionData: shape({
    name: string,
    component: string,
    position: number,
    type: string,
  }),
  isImpressionsRequired: bool,
  unavailableTooltipMessage: string,
};

Swatch.defaultProps = {
  displayInToolTip: false,
  hoveredCombinationDetails: {},
  miniSwatch: false,
  isTooltipsEnabled: false,
  size: null,
  swatchItemId: null,
  index: 0,
  componentImpressionData: {
    name: '',
    component: '',
    position: 1,
    type: '',
  },
  isImpressionsRequired: false,
  unavailableTooltipMessage: '',
};